import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useMeetRoom } from "@/lib/webrtc";
import { Broadcast, ShieldCheck, Eye, PaperPlaneRight, ChatCircle, BellRinging, CheckCircle, CalendarPlus } from "@phosphor-icons/react";

const asset = (u) => (u && u.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

export default function Watch() {
  const { code } = useParams();
  const [info, setInfo] = useState(undefined);

  useEffect(() => {
    let stop = false;
    const poll = async () => {
      try { const { data } = await api.get(`/watch/${code}`); if (!stop) setInfo(data); }
      catch { if (!stop) setInfo(null); }
    };
    poll();
    const iv = setInterval(poll, 8000);
    return () => { stop = true; clearInterval(iv); };
  }, [code]);

  if (info === undefined) return <div className="grid min-h-screen place-items-center bg-black text-white/60">Loading stream…</div>;
  if (info === null) return <div className="grid min-h-screen place-items-center bg-black text-white/60" data-testid="watch-not-found">This stream link is invalid.</div>;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="watch-page">
      <div className="flex items-center gap-3 px-4 py-3">
        {info.branding?.logo_url && <img src={asset(info.branding.logo_url)} alt="" className="h-7 w-auto max-w-[110px] object-contain" />}
        <div className="min-w-0">
          <p className="truncate font-display font-semibold">{info.title}</p>
          {info.branding?.brand_name && <p className="text-xs text-white/40">{info.branding.brand_name}</p>}
        </div>
        <span className={`ml-auto flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${info.live ? "bg-red-600" : info.replay ? "bg-white/10 text-white/70" : "bg-white/10 text-white/60"}`} data-testid="watch-status">
          <Broadcast size={13} weight="fill" /> {info.live ? "LIVE" : info.replay ? "Replay" : info.status === "ended" ? "Ended" : "Offline"}
        </span>
      </div>
      {info.live && info.room_code
        ? <Viewer code={info.room_code} accent={info.branding?.brand_color} />
        : info.replay && info.replay_url
        ? <ReplayViewer url={asset(info.replay_url)} accent={info.branding?.brand_color} />
        : <div className="grid h-[70vh] place-items-center text-center text-white/50">
            <div>
              <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10"><Broadcast size={26} /></div>
              {info.status === "ended" || info.status === "cancelled"
                ? <p className="font-display text-lg">This broadcast has ended.</p>
                : info.scheduled_at
                  ? <><p className="font-display text-lg">Premiere starts soon</p><Countdown to={info.scheduled_at} /><CalendarButtons title={info.title} at={info.scheduled_at} />{info.registrations_open && <Rsvp code={code} />}</>
                  : <p className="font-display text-lg">The broadcast hasn't started yet.</p>}
              <p className="mt-1 text-sm text-white/40">This page updates automatically when the host goes live.</p>
            </div>
          </div>}
      <p className="flex items-center justify-center gap-1.5 py-3 text-[11px] text-white/30"><ShieldCheck size={12} weight="fill" /> Powered by iYeneh Connect</p>
    </div>
  );
}

function Viewer({ code, accent }) {
  const [name, setName] = useState(() => localStorage.getItem("watch:name") || "");
  const [asked, setAsked] = useState(() => !!localStorage.getItem("watch:name"));
  if (!asked) return <NamePrompt onDone={(n) => { const v = (n || "Guest").trim().slice(0, 40); localStorage.setItem("watch:name", v); setName(v); setAsked(true); }} />;
  return <LiveViewer code={code} accent={accent} name={name || "Guest"} />;
}

function NamePrompt({ onDone }) {
  const [v, setV] = useState("");
  return (
    <div className="mx-auto max-w-md px-4" data-testid="watch-name-prompt">
      <div className="rounded-2xl border border-white/10 bg-[#0b0d13] p-5 text-center">
        <div className="mx-auto mb-2 flex h-11 w-11 items-center justify-center rounded-xl bg-white/10"><ChatCircle size={22} /></div>
        <p className="font-display text-lg">Join the live chat</p>
        <p className="mb-3 mt-1 text-sm text-white/40">Pick a name others will see. You can just watch too.</p>
        <input data-testid="watch-name-input" value={v} onChange={(e) => setV(e.target.value)} placeholder="Your name" maxLength={40}
          className="mb-2 w-full rounded-full border border-white/10 bg-white/5 px-4 py-2.5 text-center outline-none focus:border-white/30" />
        <button data-testid="watch-name-go" onClick={() => onDone(v)} className="w-full rounded-full bg-red-600 py-2.5 text-sm font-semibold">Enter the stream</button>
      </div>
    </div>
  );
}

function LiveViewer({ code, accent, name }) {
  const room = useMeetRoom({ code, name, isHost: false, viewOnly: true });
  const big = room.remotes.find((r) => r.stream) || room.remotes[0];
  const [text, setText] = useState("");
  const listRef = useRef(null);
  useEffect(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, [room.watchChat]);
  const send = () => { if (text.trim()) { room.sendWatchChat(text.trim()); setText(""); } };

  return (
    <div className="mx-auto grid max-w-6xl gap-4 px-4 lg:grid-cols-[1fr_320px]" data-testid="watch-viewer">
      <div>
        <div className="relative aspect-video overflow-hidden rounded-2xl border border-white/10 bg-[#0b0d13]" style={accent ? { borderColor: accent } : undefined}>
          {big ? <RemoteVideo stream={big.stream} /> : <div className="grid h-full place-items-center text-white/40">Connecting to the live stream…</div>}
          <span className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full bg-black/60 px-3 py-1 text-xs font-semibold backdrop-blur" data-testid="watch-viewer-count">
            <Eye size={13} weight="fill" /> {room.viewerCount} watching
          </span>
        </div>
      </div>
      <div className="flex h-[60vh] flex-col rounded-2xl border border-white/10 bg-[#0b0d13] lg:h-auto" data-testid="watch-chat">
        <div className="flex items-center gap-2 border-b border-white/10 px-4 py-3 text-sm font-semibold"><ChatCircle size={16} weight="duotone" /> Live chat</div>
        <div ref={listRef} className="flex-1 space-y-2 overflow-y-auto px-4 py-3 text-sm">
          {room.watchChat.length === 0 && <p className="text-xs text-white/30">No messages yet. Say hello 👋</p>}
          {room.watchChat.map((m) => (
            <div key={m.id} data-testid={`watch-msg-${m.id}`} className="leading-snug">
              <span className="font-semibold text-white/80" style={accent ? { color: accent } : undefined}>{m.name}</span>
              <span className="ml-1.5 text-white/70">{m.text}</span>
            </div>
          ))}
        </div>
        {room.chatLocked ? (
          <div className="border-t border-white/10 px-4 py-3 text-center text-xs text-white/40" data-testid="watch-chat-locked">The host paused the chat.</div>
        ) : (
          <div className="flex items-center gap-2 border-t border-white/10 px-3 py-2.5">
            <input data-testid="watch-chat-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Send a message" maxLength={400} className="min-w-0 flex-1 rounded-full bg-white/5 px-4 py-2 text-sm outline-none focus:bg-white/10" />
            <button data-testid="watch-chat-send" onClick={send} className="flex h-9 w-9 items-center justify-center rounded-full bg-red-600"><PaperPlaneRight size={16} weight="fill" /></button>
          </div>
        )}
      </div>
    </div>
  );
}

function RemoteVideo({ stream }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current && stream) ref.current.srcObject = stream; }, [stream]);
  return <video ref={ref} autoPlay playsInline className="h-full w-full object-contain" data-testid="watch-video" />;
}

function ReplayViewer({ url, accent }) {
  return (
    <div className="mx-auto max-w-4xl px-4" data-testid="watch-replay">
      <div className="relative aspect-video overflow-hidden rounded-2xl border border-white/10 bg-[#0b0d13]" style={accent ? { borderColor: accent } : undefined}>
        <video src={url} controls autoPlay playsInline className="h-full w-full object-contain" data-testid="watch-replay-video" />
        <span className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full bg-black/60 px-3 py-1 text-xs font-semibold backdrop-blur">
          <Broadcast size={13} weight="fill" /> Replay
        </span>
      </div>
      <p className="mt-3 text-center text-sm text-white/40">You're watching the replay of this broadcast.</p>
    </div>
  );
}

function Countdown({ to }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const iv = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(iv); }, []);
  const ms = new Date(to).getTime() - now;
  if (ms <= 0) return <p className="mt-2 text-sm text-emerald-400" data-testid="watch-countdown">Starting any moment now…</p>;
  const s = Math.floor(ms / 1000), d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60), ss = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return (
    <p className="mt-2 font-mono-metric text-3xl font-semibold text-white" data-testid="watch-countdown">
      {d > 0 ? `${d}d ` : ""}{pad(h)}:{pad(m)}:{pad(ss)}
    </p>
  );
}

function CalendarButtons({ title, at }) {
  const start = new Date(at);
  const end = new Date(start.getTime() + 3600000);
  const fmt = (d) => d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  const url = typeof window !== "undefined" ? window.location.href : "";
  const gcal = `https://calendar.google.com/calendar/render?${new URLSearchParams({ action: "TEMPLATE", text: title || "Live premiere", dates: `${fmt(start)}/${fmt(end)}`, details: `Watch live: ${url}`, location: url }).toString()}`;
  const ics = () => {
    const body = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//iYeneh Connect//Premiere//EN", "BEGIN:VEVENT", `UID:${Date.now()}@iyeneh`, `DTSTAMP:${fmt(new Date())}`, `DTSTART:${fmt(start)}`, `DTEND:${fmt(end)}`, `SUMMARY:${title || "Live premiere"}`, `DESCRIPTION:Watch live: ${url}`, `URL:${url}`, "END:VEVENT", "END:VCALENDAR"].join("\r\n");
    const blob = new Blob([body], { type: "text/calendar" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "premiere.ics"; a.click(); URL.revokeObjectURL(a.href);
  };
  return (
    <div className="mx-auto mt-4 flex max-w-xs items-center justify-center gap-2" data-testid="watch-calendar">
      <a data-testid="cal-google" href={gcal} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs font-semibold hover:border-white/40"><CalendarPlus size={14} weight="bold" /> Google Calendar</a>
      <button data-testid="cal-ics" onClick={ics} className="flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs font-semibold hover:border-white/40"><CalendarPlus size={14} weight="bold" /> Apple / Outlook</button>
    </div>
  );
}


function Rsvp({ code }) {
  const [email, setEmail] = useState(() => localStorage.getItem(`rsvp:${code}`) || "");
  const [done, setDone] = useState(() => !!localStorage.getItem(`rsvp:${code}`));
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!email.includes("@")) { toast.error("Enter a valid email"); return; }
    setBusy(true);
    try {
      await api.post(`/watch/${code}/register`, { email });
      localStorage.setItem(`rsvp:${code}`, email);
      setDone(true);
      toast.success("You're on the list — we'll email you before it starts");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  if (done) return (
    <p className="mx-auto mt-5 flex max-w-xs items-center justify-center gap-1.5 text-sm text-emerald-400" data-testid="rsvp-done">
      <CheckCircle size={16} weight="fill" /> You'll get an email reminder before showtime.
    </p>
  );
  return (
    <div className="mx-auto mt-5 max-w-xs" data-testid="rsvp">
      <p className="mb-2 flex items-center justify-center gap-1.5 text-sm text-white/60"><BellRinging size={15} weight="fill" /> Remind me when it starts</p>
      <div className="flex items-center gap-2">
        <input data-testid="rsvp-email" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()}
          placeholder="you@email.com" className="min-w-0 flex-1 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm outline-none focus:border-white/30" />
        <button data-testid="rsvp-submit" disabled={busy} onClick={submit} className="rounded-full bg-red-600 px-4 py-2 text-sm font-semibold disabled:opacity-60">Notify me</button>
      </div>
    </div>
  );
}
